import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { formatCentsForDisplay } from '@/utils/money';
import type { WorkOrderSignerRole, WorkOrderSignatureStatus } from '@/types';
import { SignatureCanvas } from './SignatureCanvas';
import { priceBasisLabel, signerRoleLabel } from './signatureLabels';

export interface TabletSignatureDialogProps {
  open: boolean;
  status: WorkOrderSignatureStatus;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: {
    signerName: string;
    signerRole: WorkOrderSignerRole;
    signatureImage: string;
    consentTextVersion: number;
  }) => Promise<void>;
}

export function TabletSignatureDialog({
  open,
  status,
  submitting,
  onClose,
  onSubmit,
}: TabletSignatureDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const roles: WorkOrderSignerRole[] =
    status.allowedSignerRoles.length > 0 ? status.allowedSignerRoles : ['orderer'];
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState<WorkOrderSignerRole>(roles[0] ?? 'orderer');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [canvasKey, setCanvasKey] = useState(0);
  const [nameError, setNameError] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const reset = () => {
    setSignerName('');
    setSignerRole(roles[0] ?? 'orderer');
    setSignatureImage(null);
    setCanvasKey((key) => key + 1);
    setNameError(false);
    setSignatureError(false);
    setSubmitError(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const name = signerName.trim();
    const missingName = name.length === 0;
    const missingSignature = signatureImage == null || signatureImage.length === 0;
    setNameError(missingName);
    setSignatureError(missingSignature);
    if (missingName || missingSignature) return;
    setSubmitError(false);
    try {
      await onSubmit({
        signerName: name,
        signerRole,
        signatureImage,
        consentTextVersion: status.settings.consentTextVersion,
      });
      reset();
    } catch {
      setSubmitError(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      fullScreen
      mobileTitle={t('signature.tabletTitle')}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('signature.tabletTitle')}
        <Tooltip title={tc('close')}>
          <IconButton onClick={handleClose} aria-label={tc('close')} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t('signature.scopeTitle')}
            </Typography>
            <List dense disablePadding>
              {status.scope.lines.map((line) => (
                <ListItem key={line.key} disableGutters>
                  <ListItemText
                    primary={line.description}
                    secondary={t('signature.lineQuantity', { quantity: line.quantity })}
                  />
                  <Typography variant="body2">{formatCentsForDisplay(line.lineTotalCents)}</Typography>
                </ListItem>
              ))}
            </List>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                {t('signature.scopePriceBasis')}: {priceBasisLabel(t, status.scope.priceBasis)}
              </Typography>
              <Typography variant="body2">
                {t('signature.scopeAdditionalWorkLimit')}:{' '}
                {formatCentsForDisplay(status.scope.additionalWorkLimitCents)}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('signature.scopeTotal')}: {formatCentsForDisplay(status.scope.grossTotalCents)}
              </Typography>
            </Stack>
          </Box>

          {status.settings.consentText ? (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('signature.consentTitle')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {status.settings.consentText}
              </Typography>
            </Box>
          ) : null}

          <TextField
            label={t('signature.signerName')}
            value={signerName}
            onChange={(event) => {
              setSignerName(event.target.value);
              setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? t('signature.signerNameRequired') : undefined}
            fullWidth
            autoComplete="name"
          />

          <FormControl fullWidth>
            <InputLabel id="tablet-signer-role-label">{t('signature.signerRole')}</InputLabel>
            <Select
              labelId="tablet-signer-role-label"
              label={t('signature.signerRole')}
              value={signerRole}
              onChange={(event) => setSignerRole(event.target.value as WorkOrderSignerRole)}
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {signerRoleLabel(t, role)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('signature.canvasAria')}
              </Typography>
              <Tooltip title={t('signature.clearSignature')}>
                <IconButton
                  onClick={() => {
                    setSignatureImage(null);
                    setCanvasKey((key) => key + 1);
                    setSignatureError(false);
                  }}
                  aria-label={t('signature.clearSignature')}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <SignatureCanvas
              key={canvasKey}
              ariaLabel={t('signature.canvasAria')}
              onChange={setSignatureImage}
              disabled={submitting}
            />
            {signatureError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t('signature.signatureRequired')}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {t('signature.tabletSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
